import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {BehaviorSubject, Subscription} from 'rxjs';
import {switchMap} from 'rxjs/operators';
import {getEffectivePosition, getEffectiveZIndex} from '../../../utils';
import {ModalService} from '../modal.service';
import {StepState, TargetConfig, TourService} from '../tour.service';

const ARROW_SIZE = 20;
const ARROW_OFFSET = 30;

@Component({
  selector: 'tour-step',
  templateUrl: './tour-step.component.html',
  styleUrls: ['./tour-step.component.scss'],
  // changeDetection: ChangeDetectionStrategy.OnPush
})
export class TourStepComponent implements OnInit, OnChanges, OnDestroy, AfterViewChecked {

  @Input() name: string;
  @Input() target: string | string[];

  @ViewChild('content') content: ElementRef;

  state$?: BehaviorSubject<StepState>;
  visible = false;
  positioned = false;
  targetConfig: TargetConfig | null = null;
  style: Partial<CSSStyleDeclaration> = {};

  private repositionTimeout: number | null = null;
  private stateSub: Subscription | null = null;
  private targetSub: Subscription | null = null;
  private modalSub: Subscription | null = null;

  constructor(private Tour: TourService,
              private Changes: ChangeDetectorRef,
              private Modal: ModalService) {
  }

  async next() {
    if (this.targetConfig && this.targetConfig.component) {
      this.targetConfig.component.tourStepComplete.emit(this.name);
    }
    const currentTour = this.state$ && this.state$.getValue().currentTour;
    if (currentTour) await this.Tour.nextStep(currentTour);
  }

  stop() {
    const currentTour = this.state$ && this.state$.getValue().currentTour;
    if (currentTour) this.Tour.stopTour(currentTour);
  }

  ngOnInit() {
    this.modalSub = this.Modal.visible$.subscribe(() => this.reposition());
  }

  ngOnChanges(changes: SimpleChanges) {
    const nameChange = changes['name'];
    if (nameChange) {
      if (this.state$) this.state$ = null;
      if (this.stateSub) this.stateSub.unsubscribe();
      if (this.name) {
        this.state$ = this.Tour.getStepState(this.name);
        this.stateSub = this.state$.subscribe(() => this.reposition());
      }
    }
    const targetChange = changes['target'];
    if ((targetChange || nameChange) && this.target && this.state$) {
      if (this.targetSub) this.targetSub.unsubscribe();
      this.targetSub = this.state$
        .pipe(switchMap(state => this.Tour.getStepTargets(state.currentTour, this.target)))
        .subscribe(config => {
          this.targetConfig = config;
          this.reposition();
        });
    }
  }

  ngAfterViewChecked() {
    this.reposition();
  }

  ngOnDestroy() {
    if (this.repositionTimeout) {
      window.clearTimeout(this.repositionTimeout);
      this.repositionTimeout = null;
    }
    if (this.targetSub) {
      this.targetSub.unsubscribe();
      this.targetSub = null;
    }
    if (this.modalSub) {
      this.modalSub.unsubscribe();
      this.modalSub = null;
    }
  }

  @HostListener('click', ['$event'])
  click(event: MouseEvent) {
    event.stopPropagation();
    event.preventDefault();
  }

  @HostListener('window:resize')
  windowResize() {
    this.reposition();
  }

  private reposition() {
    if (this.content) {
      if (this.repositionTimeout) return;
      this.repositionTimeout = window.setTimeout(() => {
        if (!this.targetSub) {
          // We're destroyed!
          return;
        }

        // Handle visibility changes before repositioning
        const config = this.targetConfig;
        if (this.state$ && this.state$.getValue().active && !this.Modal.visible$.getValue()
          && config && config.component && config.component.elem
          && config.component.elem.nativeElement.offsetHeight) {
          if (!this.visible) {
            this.visible = true;
            this.Changes.detectChanges();
            this.repositionTimeout = null;
            this.reposition();
            return;
          }
        } else {
          if (this.visible) {
            this.visible = false;
            this.Changes.detectChanges();
          }
          this.repositionTimeout = null;
          return;
        }

        if (config && config.component && config.component.elem && this.content) {
          const targetElem: HTMLElement = config.component.elem.nativeElement;
          const contentElem: HTMLElement = this.content.nativeElement;

          this.style.position = getEffectivePosition(targetElem) === 'fixed' ? 'fixed' : 'absolute';
          this.style.zIndex = (getEffectiveZIndex(targetElem) + 1).toString();

          const targetBoundingRect = targetElem.getBoundingClientRect();
          const targetOffsetLeft = targetBoundingRect.left
            + (this.style.position === 'fixed' ? 0 : document.documentElement.scrollLeft);
          const targetOffsetTop = targetBoundingRect.top
            + (this.style.position === 'fixed' ? 0 : document.documentElement.scrollTop);

          if (!config.attachment || config.attachment === 'bottom') {
            this.style.left = (targetOffsetLeft - ARROW_OFFSET) + 'px';
            this.style.top = (targetOffsetTop + targetElem.offsetHeight + (ARROW_SIZE * 0.7)) + 'px';
          } else if (config.attachment === 'left') {
            this.style.left = (targetOffsetLeft - contentElem.offsetWidth - (ARROW_SIZE * 0.7)) + 'px';
            this.style.top = (targetOffsetTop - ARROW_OFFSET + (targetElem.offsetHeight / 2)) + 'px';
          }

          this.positioned = true;
        }

        this.Changes.detectChanges();
        this.repositionTimeout = null;
      }, 0);
    }
  }
}
